import { FlatList, Text } from "react-native";
import { View } from "react-native";
import { CheckboxItem, ScrollableContainer, TextInput, useErrorScroll, ScreenContainer } from "../components";
import { useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import FacilityPickerInput, { Facility } from "../components/FacilityPickerInput";
import JobTitlePickerInput from "../components/JobTitlePickerInput";
import TextInputArea from "../components/TextInputArea";
import DatePickerInput from "../components/DatePickerInput";
import { ScreenProps } from "./AccountScreen/types";

export const navigationTitle = "Add Employment";

type FormData = {
  facility: null | Facility;
  jobTitle: string;
  otherJobTitle?: string;
  jobDescription: string;
  effectiveDate: string;
  isPrimaryFacility: boolean;
  hasVerifiedEligibility: boolean;
};

const defaultValues: FormData = {
  facility: null,
  jobTitle: "",
  otherJobTitle: "",
  jobDescription: "",
  effectiveDate: "",
  isPrimaryFacility: false,
  hasVerifiedEligibility: false,
};

const AddEmploymentScreen = ({ navigation }: ScreenProps<"AddEmployment">) => {
  const flatListRef = useRef<FlatList>(null);
  const { Divider } = useErrorScroll({ defaultValues, flatListRef });

  const { control, watch } = useForm<FormData>({ defaultValues, mode: "onChange" });

  const jobTitle = watch("jobTitle");

  const navigateToAccountEdit = () => navigation.navigate("AccountEdit");

  return (
    <ScreenContainer navigationTitle={navigationTitle} testID="AddEmploymentScreen" goBack={navigateToAccountEdit}>
      <ScrollableContainer>
        <View className="p-6 justify-between">
          <Text className="text-regular-tight font-700 text-ink-darkest">Employment Details</Text>

          <Text className="text-regular-tight font-400 text-ink-darker mt-4 mb-2">
            Lorem ipsum dolor sit amet consectetur. Quis mauris laoreet amet a quam commodo arcu arcu.
          </Text>
        </View>

        <Divider field="facility" />
        <View className="p-6 justify-between">
          <Text className="text-regular-tight font-700 text-ink-darkest">Facility</Text>
          <Controller
            name="facility"
            control={control}
            rules={{
              required: "You must select a facility",
            }}
            render={({ field: { onChange, value } }) => (
              <FacilityPickerInput
                title="Select Facility"
                placeholder="Facility Name"
                onChange={onChange}
                value={value || undefined}
              />
            )}
          />
        </View>

        <Divider field="jobTitle" />

        <View className="p-6 justify-between">
          <Text className="text-regular-tight font-700 text-ink-darkest">Job Title</Text>

          <Text className="text-regular-tight font-400 text-ink-darker mt-4 mb-2">
            Lorem ipsum dolor sit amet consectetur. Quis mauris laoreet amet a quam commodo arcu arcu.
          </Text>
          <Controller
            name="jobTitle"
            control={control}
            rules={{
              required: "You must select a job title",
            }}
            render={({ field: { onChange, value } }) => (
              <JobTitlePickerInput onChange={onChange} value={value} placeholder="Job Title" />
            )}
          />
          {jobTitle === "Other" ? (
            <View className="mt-2">
              <Controller
                name="otherJobTitle"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextInput onChangeText={(text) => onChange(text)} value={value} placeholder="Add Your Job Title" />
                )}
              />
            </View>
          ) : null}
        </View>

        <Divider field="jobDescription" />

        <View className="p-6 justify-between">
          <Text className="text-regular-tight font-700 text-ink-darkest">Job Description</Text>

          <Text className="text-regular-tight font-400 text-ink-darker mt-4 mb-2">
            Lorem ipsum dolor sit amet consectetur. Quis mauris laoreet amet a quam commodo arcu arcu.
          </Text>

          <Controller
            name="jobDescription"
            control={control}
            rules={{
              required: "You must enter a job description",
            }}
            render={({ field: { onChange, value } }) => (
              <TextInputArea
                onChangeText={(text) => onChange(text)}
                value={value}
                placeholder="Ex. I own and operate a golf shop at a PGA Recognized Golf Facility."
              />
            )}
          />
        </View>

        <Divider field="effectiveDate" />

        <View className="p-6 justify-between">
          <Text className="text-regular-tight font-700 text-ink-darkest">Effective Date</Text>

          <Text className="text-regular-tight font-400 text-ink-darker mt-4 mb-2">
            Please select the start date for employment.
          </Text>

          <Controller
            name="effectiveDate"
            control={control}
            rules={{
              required: "You must enter an effective date",
            }}
            render={({ field: { onChange, value } }) => (
              <DatePickerInput
                onChange={onChange}
                value={value}
                placeholder="Start Date"
                title="Start Date"
                description="Select the start date for employment."
                onBlur={() => {}}
              />
            )}
          />
        </View>

        <Divider field="isPrimaryFacility" />

        <View className="p-6 justify-between mb-2">
          <Text className="text-regular-tight font-700 text-ink-darkest">Facility Type</Text>

          <Text className="text-regular-tight font-400 text-ink-darker mt-4 mb-2">
            Text explaining what defines a primary facility and why setting a primary facility is important.
          </Text>

          <Controller
            name="isPrimaryFacility"
            control={control}
            render={({ field: { onChange, value } }) => (
              <View style={{ marginLeft: -8 }}>
                <CheckboxItem
                  value={value}
                  onChange={onChange}
                  label="This is my primary employment/facility"
                  labelStyle="text-regular-normal text-left mx-0"
                  className="items-center px-0"
                />
              </View>
            )}
          />
        </View>

        <Divider field="hasVerifiedEligibility" />

        <View className="p-6 align-top">
          <Controller
            name="hasVerifiedEligibility"
            control={control}
            render={({ field: { onChange, value } }) => (
              <View className="flex-1 align-top justify-start" style={{ marginLeft: -8 }}>
                <CheckboxItem
                  value={value}
                  onChange={onChange}
                  label="I verify that I meet the eligible employment requirements as defined by the PGA Constitution and Bylaws for the classification and employment I have submitted. The employment must show a pattern of employment that is regular, continuous, at the place of employment and provide the public with golf related goods and/or services. Members and Associates are cautioned to be factual, as falsification of information could result in disciplinary action."
                  labelStyle="text-regular-normal text-left mx-0"
                  className="items-start px-0"
                />
              </View>
            )}
          />
        </View>
      </ScrollableContainer>
    </ScreenContainer>
  );
};

export default AddEmploymentScreen;
